import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function RiskManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [statistics, setStatistics] = useState(null);
  
  // Data states
  const [alerts, setAlerts] = useState([]);
  const [highRiskUsers, setHighRiskUsers] = useState([]);
  const [pepEntries, setPepEntries] = useState([]);
  const [watchlistEntries, setWatchlistEntries] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [sars, setSars] = useState([]);

  // Filters
  const [alertFilter, setAlertFilter] = useState({ status: "", severity: "" });
  const [watchlistFilter, setWatchlistFilter] = useState({ type: "", search: "" });
  const [pepSearch, setPepSearch] = useState("");

  // Modals
  const [showScreenModal, setShowScreenModal] = useState(false);
  const [showPepModal, setShowPepModal] = useState(false);
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showSarModal, setShowSarModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // Selected items
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Form states
  const [screenForm, setScreenForm] = useState({ userId: "", fullName: "", bvn: "", nin: "", screenPep: true, screenWatchlist: true, screenSanctions: true });
  const [pepForm, setPepForm] = useState({ fullName: "", position: "", organization: "", country: "Nigeria", pepCategory: "Domestic", pepLevel: "Primary", notes: "", source: "" });
  const [watchlistForm, setWatchlistForm] = useState({ fullName: "", watchlistType: "", severity: "High", reason: "", caseReference: "", source: "" });
  const [sarForm, setSarForm] = useState({ userId: "", reportType: "SAR", suspiciousActivity: "", totalAmountInvolved: "", notes: "" });

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === "alerts") loadAlerts();
    else if (activeTab === "pep") loadPepEntries();
    else if (activeTab === "watchlist") loadWatchlistEntries();
    else if (activeTab === "reviews") loadPendingReviews();
    else if (activeTab === "sar") loadSars();
    else if (activeTab === "highrisk") loadHighRiskUsers();
  }, [activeTab, alertFilter, watchlistFilter, pepSearch]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, statsRes] = await Promise.all([
        api.get("/Risk/dashboard"),
        api.get("/Risk/statistics?period=Month")
      ]);
      setDashboard(dashRes.data);
      setStatistics(statsRes.data);
      setAlerts(dashRes.data.recentAlerts || []);
      setHighRiskUsers(dashRes.data.highRiskUsers || []);
    } catch (err) {
      console.error("Error loading dashboard:", err);
      if (err.response?.status === 403) {
        alert("Access denied. Admin privileges required.");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const res = await api.get(`/Risk/alerts?status=${alertFilter.status}&severity=${alertFilter.severity}`);
      setAlerts(res.data);
    } catch (err) { console.error("Error loading alerts:", err); }
  };

  const loadPepEntries = async () => {
    try {
      const res = await api.get(`/Risk/pep?search=${pepSearch}&isActive=true`);
      setPepEntries(res.data);
    } catch (err) { console.error("Error loading PEP entries:", err); }
  };

  const loadWatchlistEntries = async () => {
    try {
      const res = await api.get(`/Risk/watchlist?type=${watchlistFilter.type}&search=${watchlistFilter.search}&isActive=true`);
      setWatchlistEntries(res.data);
    } catch (err) { console.error("Error loading watchlist:", err); }
  };

  const loadPendingReviews = async () => {
    try {
      const res = await api.get("/Risk/screen/pending");
      setPendingReviews(res.data);
    } catch (err) { console.error("Error loading pending reviews:", err); }
  };

  const loadSars = async () => {
    try {
      const res = await api.get("/Risk/sar");
      setSars(res.data);
    } catch (err) { console.error("Error loading SARs:", err); }
  };

  const loadHighRiskUsers = async () => {
    try {
      const res = await api.get("/Risk/profile/high-risk");
      setHighRiskUsers(res.data);
    } catch (err) { console.error("Error loading high risk users:", err); }
  };

  const handleScreenUser = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/Risk/screen", screenForm);
      alert(`✅ Screening completed! ${res.data.matches} potential matches found.`);
      setShowScreenModal(false);
      setScreenForm({ userId: "", fullName: "", bvn: "", nin: "", screenPep: true, screenWatchlist: true, screenSanctions: true });
      loadDashboard();
    } catch (err) {
      alert(err.response?.data?.message || "Screening failed");
    }
  };

  const handleAddPep = async (e) => {
    e.preventDefault();
    try {
      await api.post("/Risk/pep", pepForm);
      alert("✅ PEP entry added!");
      setShowPepModal(false);
      setPepForm({ fullName: "", position: "", organization: "", country: "Nigeria", pepCategory: "Domestic", pepLevel: "Primary", notes: "", source: "" });
      loadPepEntries();
      loadDashboard();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add PEP");
    }
  };

  const handleAddWatchlist = async (e) => {
    e.preventDefault();
    try {
      await api.post("/Risk/watchlist", watchlistForm);
      alert("✅ Watchlist entry added!");
      setShowWatchlistModal(false);
      setWatchlistForm({ fullName: "", watchlistType: "", severity: "High", reason: "", caseReference: "", source: "" });
      loadWatchlistEntries();
      loadDashboard();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add watchlist entry");
    }
  };

  const handleResolveAlert = async (alertId, status, resolution, action) => {
    try {
      await api.post("/Risk/alerts/resolve", { alertId, status, resolution, resolutionAction: action });
      alert(`✅ Alert ${status === "Resolved" ? "resolved" : "marked as false positive"}!`);
      loadAlerts();
      loadDashboard();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to resolve alert");
    }
  };

  const handleReviewScreening = async (screeningId, status, notes) => {
    try {
      await api.post("/Risk/screen/review", { screeningId, reviewStatus: status, reviewNotes: notes });
      alert(`✅ Screening marked as ${status}!`);
      loadPendingReviews();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to review screening");
    }
  };

  const handleBlockUser = async (userId, reason) => {
    if (!window.confirm("Are you sure you want to block this user's transactions?")) return;
    try {
      await api.post(`/Risk/profile/${userId}/block`, { reason });
      alert("✅ User transactions blocked!");
      loadHighRiskUsers();
      loadDashboard();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to block user");
    }
  };

  const handleUnblockUser = async (userId) => {
    if (!window.confirm("Are you sure you want to unblock this user?")) return;
    try {
      await api.post(`/Risk/profile/${userId}/unblock`);
      alert("✅ User transactions unblocked!");
      loadHighRiskUsers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to unblock user");
    }
  };

  const handleCreateSar = async (e) => {
    e.preventDefault();
    try {
      await api.post("/Risk/sar", sarForm);
      alert("✅ SAR created!");
      setShowSarModal(false);
      setSarForm({ userId: "", reportType: "SAR", suspiciousActivity: "", totalAmountInvolved: "", notes: "" });
      loadSars();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create SAR");
    }
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;
  const formatDate = (date) => date ? new Date(date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "Critical": return "#dc2626";
      case "High": return "#ea580c";
      case "Medium": return "#d97706";
      default: return "#16a34a";
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case "Critical": return "#dc2626";
      case "High": return "#ea580c";
      case "Medium": return "#d97706";
      default: return "#16a34a";
    }
  };

  const styles = {
    container: { padding: "20px", maxWidth: "1600px", margin: "0 auto", fontFamily: "Arial, sans-serif", background: "#f5f5f5", minHeight: "100vh" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", background: "white", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    title: { fontSize: "24px", fontWeight: "bold", color: "#1a1a2e", display: "flex", alignItems: "center", gap: "10px" },
    backBtn: { padding: "10px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
    tabs: { display: "flex", gap: "5px", marginBottom: "20px", background: "white", padding: "10px", borderRadius: "12px", flexWrap: "wrap" },
    tab: { padding: "12px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", transition: "all 0.3s" },
    tabActive: { background: "#1a1a2e", color: "white" },
    tabInactive: { background: "#f3f4f6", color: "#374151" },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "15px", marginBottom: "20px" },
    statCard: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    statValue: { fontSize: "32px", fontWeight: "bold" },
    statLabel: { fontSize: "13px", color: "#6b7280", marginTop: "5px" },
    card: { background: "white", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" },
    cardTitle: { fontSize: "18px", fontWeight: "bold", color: "#1a1a2e" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: "600", color: "#374151", fontSize: "13px" },
    td: { padding: "12px", borderBottom: "1px solid #e5e7eb", fontSize: "14px" },
    badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "600", display: "inline-block" },
    btn: { padding: "8px 16px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", fontSize: "13px" },
    btnPrimary: { background: "#1a1a2e", color: "white" },
    btnSuccess: { background: "#16a34a", color: "white" },
    btnDanger: { background: "#dc2626", color: "white" },
    btnWarning: { background: "#d97706", color: "white" },
    btnSecondary: { background: "#e5e7eb", color: "#374151" },
    actionBtn: { padding: "6px 12px", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", marginRight: "5px" },
    modal: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" },
    modalContent: { background: "white", borderRadius: "12px", padding: "25px", width: "100%", maxWidth: "600px", maxHeight: "90vh", overflow: "auto" },
    modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", paddingBottom: "15px", borderBottom: "1px solid #e5e7eb" },
    modalTitle: { fontSize: "20px", fontWeight: "bold", color: "#1a1a2e" },
    closeBtn: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#6b7280" },
    form: { display: "grid", gap: "15px" },
    formGroup: { display: "flex", flexDirection: "column", gap: "5px" },
    label: { fontWeight: "600", color: "#374151", fontSize: "14px" },
    input: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
    select: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
    textarea: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", minHeight: "100px", resize: "vertical" },
    checkbox: { display: "flex", alignItems: "center", gap: "8px" },
    filterRow: { display: "flex", gap: "10px", marginBottom: "15px", flexWrap: "wrap" }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "24px" }}>Loading Risk Management...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <span>🛡️</span> Risk Management & Compliance
        </h1>
        <div style={{ display: "flex", gap: "10px" }}>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => setShowScreenModal(true)}>
            🔍 Screen User
          </button>
          <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Back</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: "dashboard", label: "📊 Dashboard", count: null },
          { id: "alerts", label: "🚨 Alerts", count: dashboard?.openAlerts },
          { id: "reviews", label: "📋 Pending Reviews", count: dashboard?.pendingReviews },
          { id: "highrisk", label: "⚠️ High Risk Users", count: dashboard?.totalHighRiskUsers },
          { id: "pep", label: "🏛️ PEP Database", count: dashboard?.totalPepEntries },
          { id: "watchlist", label: "📝 Watchlist", count: dashboard?.totalWatchlistEntries },
          { id: "sar", label: "📑 SAR Reports", count: dashboard?.draftSars + dashboard?.pendingSars }
        ].map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : styles.tabInactive) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label} {tab.count > 0 && <span style={{ marginLeft: "5px", background: activeTab === tab.id ? "white" : "#dc2626", color: activeTab === tab.id ? "#1a1a2e" : "white", padding: "2px 8px", borderRadius: "10px", fontSize: "11px" }}>{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && (
        <>
          {/* Stats Grid */}
          <div style={styles.statsGrid}>
            <div style={{ ...styles.statCard, borderTop: "4px solid #dc2626" }}>
              <div style={{ ...styles.statValue, color: "#dc2626" }}>{dashboard?.totalHighRiskUsers || 0}</div>
              <div style={styles.statLabel}>High Risk Users</div>
            </div>
            <div style={{ ...styles.statCard, borderTop: "4px solid #ea580c" }}>
              <div style={{ ...styles.statValue, color: "#ea580c" }}>{dashboard?.openAlerts || 0}</div>
              <div style={styles.statLabel}>Open Alerts</div>
            </div>
            <div style={{ ...styles.statCard, borderTop: "4px solid #d97706" }}>
              <div style={{ ...styles.statValue, color: "#d97706" }}>{dashboard?.criticalAlerts || 0}</div>
              <div style={styles.statLabel}>Critical Alerts</div>
            </div>
            <div style={{ ...styles.statCard, borderTop: "4px solid #7c3aed" }}>
              <div style={{ ...styles.statValue, color: "#7c3aed" }}>{dashboard?.totalPepUsers || 0}</div>
              <div style={styles.statLabel}>PEP Users</div>
            </div>
            <div style={{ ...styles.statCard, borderTop: "4px solid #0891b2" }}>
              <div style={{ ...styles.statValue, color: "#0891b2" }}>{dashboard?.totalWatchlistedUsers || 0}</div>
              <div style={styles.statLabel}>Watchlisted</div>
            </div>
            <div style={{ ...styles.statCard, borderTop: "4px solid #059669" }}>
              <div style={{ ...styles.statValue, color: "#059669" }}>{dashboard?.totalUnderMonitoring || 0}</div>
              <div style={styles.statLabel}>Under Monitoring</div>
            </div>
            <div style={{ ...styles.statCard, borderTop: "4px solid #1a1a2e" }}>
              <div style={{ ...styles.statValue, color: "#1a1a2e" }}>{dashboard?.totalBlockedUsers || 0}</div>
              <div style={styles.statLabel}>Blocked Users</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{dashboard?.screeningsToday || 0}</div>
              <div style={styles.statLabel}>Screenings Today</div>
            </div>
          </div>

          {/* Monthly Statistics */}
          {statistics && (
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>📈 Monthly Statistics</h3>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "20px" }}>
                <div><strong>{statistics.totalScreenings}</strong> <span style={{ color: "#6b7280" }}>Total Screenings</span></div>
                <div><strong>{statistics.pepMatches}</strong> <span style={{ color: "#6b7280" }}>PEP Matches</span></div>
                <div><strong>{statistics.watchlistMatches}</strong> <span style={{ color: "#6b7280" }}>Watchlist Matches</span></div>
                <div><strong>{statistics.alertsGenerated}</strong> <span style={{ color: "#6b7280" }}>Alerts Generated</span></div>
                <div><strong>{statistics.alertsResolved}</strong> <span style={{ color: "#6b7280" }}>Alerts Resolved</span></div>
                <div><strong>{statistics.falsePositives}</strong> <span style={{ color: "#6b7280" }}>False Positives</span></div>
                <div><strong>{statistics.sarsCreated}</strong> <span style={{ color: "#6b7280" }}>SARs Created</span></div>
                <div><strong>{statistics.usersBlocked}</strong> <span style={{ color: "#6b7280" }}>Users Blocked</span></div>
              </div>
            </div>
          )}

          {/* Recent Alerts */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>🚨 Recent Alerts</h3>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setActiveTab("alerts")}>View All</button>
            </div>
            {alerts.length > 0 ? (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Severity</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Title</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.slice(0, 5).map(alert => (
                    <tr key={alert.id}>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: `${getSeverityColor(alert.severity)}20`, color: getSeverityColor(alert.severity) }}>
                          {alert.severity}
                        </span>
                      </td>
                      <td style={styles.td}>{alert.alertType.replace("_", " ")}</td>
                      <td style={styles.td}><strong>{alert.userName}</strong></td>
                      <td style={styles.td}>{alert.title}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: alert.status === "Open" ? "#fee2e2" : "#fef3c7", color: alert.status === "Open" ? "#dc2626" : "#d97706" }}>
                          {alert.status}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDate(alert.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}>No recent alerts</p>
            )}
          </div>
        </>
      )}

      {/* Alerts Tab */}
      {activeTab === "alerts" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>🚨 Risk Alerts</h3>
          </div>
          <div style={styles.filterRow}>
            <select style={styles.select} value={alertFilter.status} onChange={(e) => setAlertFilter({ ...alertFilter, status: e.target.value })}>
              <option value="">All Status</option>
              <option value="Open">Open</option>
              <option value="UnderReview">Under Review</option>
              <option value="Resolved">Resolved</option>
              <option value="FalsePositive">False Positive</option>
            </select>
            <select style={styles.select} value={alertFilter.severity} onChange={(e) => setAlertFilter({ ...alertFilter, severity: e.target.value })}>
              <option value="">All Severity</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={loadAlerts}>🔄 Refresh</button>
          </div>
          {alerts.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Severity</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Title</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Assigned To</th>
                    <th style={styles.th}>Created</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map(alert => (
                    <tr key={alert.id}>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: `${getSeverityColor(alert.severity)}20`, color: getSeverityColor(alert.severity) }}>
                          {alert.severity === "Critical" ? "🔴" : alert.severity === "High" ? "🟠" : alert.severity === "Medium" ? "🟡" : "🟢"} {alert.severity}
                        </span>
                      </td>
                      <td style={styles.td}>{alert.alertType.replace(/_/g, " ")}</td>
                      <td style={styles.td}><strong>{alert.userName}</strong><br/><span style={{ fontSize: "12px", color: "#6b7280" }}>{alert.userEmail}</span></td>
                      <td style={styles.td}>{alert.title}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, 
                          background: alert.status === "Open" ? "#fee2e2" : alert.status === "Resolved" ? "#dcfce7" : "#fef3c7",
                          color: alert.status === "Open" ? "#dc2626" : alert.status === "Resolved" ? "#16a34a" : "#d97706" }}>
                          {alert.status}
                        </span>
                      </td>
                      <td style={styles.td}>{alert.assignedToName || "-"}</td>
                      <td style={styles.td}>{formatDate(alert.createdAt)}</td>
                      <td style={styles.td}>
                        {alert.status === "Open" && (
                          <>
                            <button style={{ ...styles.actionBtn, background: "#dcfce7", color: "#16a34a" }}
                              onClick={() => handleResolveAlert(alert.id, "Resolved", "Investigated and resolved", "NoAction")}>
                              ✅ Resolve
                            </button>
                            <button style={{ ...styles.actionBtn, background: "#e5e7eb", color: "#374151" }}
                              onClick={() => handleResolveAlert(alert.id, "FalsePositive", "False positive", "NoAction")}>
                              ❌ False +
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No alerts found</p>
          )}
        </div>
      )}

      {/* Pending Reviews Tab */}
      {activeTab === "reviews" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>📋 Pending Screening Reviews</h3>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={loadPendingReviews}>🔄 Refresh</button>
          </div>
          {pendingReviews.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Screened Name</th>
                    <th style={styles.th}>Match Score</th>
                    <th style={styles.th}>Match Details</th>
                    <th style={styles.th}>Screened At</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingReviews.map(review => (
                    <tr key={review.id}>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: review.screeningType === "PEP" ? "#7c3aed20" : "#0891b220", color: review.screeningType === "PEP" ? "#7c3aed" : "#0891b2" }}>
                          {review.screeningType}
                        </span>
                      </td>
                      <td style={styles.td}><strong>{review.screenedName}</strong></td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: review.matchScore >= 90 ? "#fee2e2" : review.matchScore >= 70 ? "#fef3c7" : "#dcfce7", color: review.matchScore >= 90 ? "#dc2626" : review.matchScore >= 70 ? "#d97706" : "#16a34a" }}>
                          {review.matchScore}%
                        </span>
                      </td>
                      <td style={styles.td}><small>{review.matchDetails}</small></td>
                      <td style={styles.td}>{formatDate(review.screenedAt)}</td>
                      <td style={styles.td}>
                        <button style={{ ...styles.actionBtn, background: "#fee2e2", color: "#dc2626" }}
                          onClick={() => handleReviewScreening(review.id, "Confirmed", "Confirmed match")}>
                          ⚠️ Confirm
                        </button>
                        <button style={{ ...styles.actionBtn, background: "#dcfce7", color: "#16a34a" }}
                          onClick={() => handleReviewScreening(review.id, "FalsePositive", "False positive")}>
                          ✅ False +
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No pending reviews 🎉</p>
          )}
        </div>
      )}

      {/* High Risk Users Tab */}
      {activeTab === "highrisk" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>⚠️ High Risk Users</h3>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={loadHighRiskUsers}>🔄 Refresh</button>
          </div>
          {highRiskUsers.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Risk Level</th>
                    <th style={styles.th}>Score</th>
                    <th style={styles.th}>PEP</th>
                    <th style={styles.th}>Watchlist</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {highRiskUsers.map(user => (
                    <tr key={user.id}>
                      <td style={styles.td}><strong>{user.userName}</strong><br/><span style={{ fontSize: "12px", color: "#6b7280" }}>{user.userEmail}</span></td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: `${getRiskLevelColor(user.riskLevel)}20`, color: getRiskLevelColor(user.riskLevel) }}>
                          {user.riskLevel}
                        </span>
                      </td>
                      <td style={styles.td}><strong>{user.overallRiskScore}</strong>/100</td>
                      <td style={styles.td}>
                        {user.isPep ? "🔴 Yes" : user.isPepFamily ? "🟠 Family" : user.isPepAssociate ? "🟡 Associate" : "✅ No"}
                        {user.pepPosition && <br/>}{user.pepPosition && <small>{user.pepPosition}</small>}
                      </td>
                      <td style={styles.td}>
                        {user.isWatchlisted ? <span style={{ color: "#dc2626" }}>🔴 {user.watchlistTypes}</span> : "✅ No"}
                      </td>
                      <td style={styles.td}>
                        {user.transactionsBlocked && <span style={{ ...styles.badge, background: "#fee2e2", color: "#dc2626" }}>🚫 Blocked</span>}
                        {user.isUnderMonitoring && <span style={{ ...styles.badge, background: "#fef3c7", color: "#d97706", marginLeft: "5px" }}>👁️ Monitored</span>}
                        {user.requiresEnhancedDueDiligence && <span style={{ ...styles.badge, background: "#e0f2fe", color: "#0369a1", marginLeft: "5px" }}>EDD</span>}
                      </td>
                      <td style={styles.td}>
                        {!user.transactionsBlocked ? (
                          <button style={{ ...styles.actionBtn, background: "#fee2e2", color: "#dc2626" }}
                            onClick={() => handleBlockUser(user.userId, "High risk score")}>
                            🚫 Block
                          </button>
                        ) : (
                          <button style={{ ...styles.actionBtn, background: "#dcfce7", color: "#16a34a" }}
                            onClick={() => handleUnblockUser(user.userId)}>
                            ✅ Unblock
                          </button>
                        )}
                        <button style={{ ...styles.actionBtn, background: "#e0f2fe", color: "#0369a1" }}
                          onClick={() => { setSelectedProfile(user); setShowProfileModal(true); }}>
                          👁️ View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No high risk users 🎉</p>
          )}
        </div>
      )}

      {/* PEP Database Tab */}
      {activeTab === "pep" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>🏛️ PEP Database</h3>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => setShowPepModal(true)}>+ Add PEP</button>
          </div>
          <div style={styles.filterRow}>
            <input type="text" placeholder="Search PEP entries..." style={{ ...styles.input, flex: 1 }}
              value={pepSearch} onChange={(e) => setPepSearch(e.target.value)} />
          </div>
          {pepEntries.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Position</th>
                    <th style={styles.th}>Organization</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Level</th>
                    <th style={styles.th}>Country</th>
                    <th style={styles.th}>Added</th>
                  </tr>
                </thead>
                <tbody>
                  {pepEntries.map(pep => (
                    <tr key={pep.id}>
                      <td style={styles.td}><strong>{pep.fullName}</strong>{pep.alternateNames && <br/>}{pep.alternateNames && <small style={{ color: "#6b7280" }}>AKA: {pep.alternateNames}</small>}</td>
                      <td style={styles.td}>{pep.position || "-"}</td>
                      <td style={styles.td}>{pep.organization || "-"}</td>
                      <td style={styles.td}><span style={{ ...styles.badge, background: "#7c3aed20", color: "#7c3aed" }}>{pep.pepCategory}</span></td>
                      <td style={styles.td}><span style={{ ...styles.badge, background: pep.pepLevel === "Primary" ? "#fee2e2" : "#fef3c7", color: pep.pepLevel === "Primary" ? "#dc2626" : "#d97706" }}>{pep.pepLevel}</span></td>
                      <td style={styles.td}>{pep.country}</td>
                      <td style={styles.td}>{formatDate(pep.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No PEP entries found</p>
          )}
        </div>
      )}

      {/* Watchlist Tab */}
      {activeTab === "watchlist" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>📝 Watchlist Database</h3>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => setShowWatchlistModal(true)}>+ Add Entry</button>
          </div>
          <div style={styles.filterRow}>
            <select style={styles.select} value={watchlistFilter.type} onChange={(e) => setWatchlistFilter({ ...watchlistFilter, type: e.target.value })}>
              <option value="">All Types</option>
              <option value="EFCC">EFCC</option>
              <option value="ICPC">ICPC</option>
              <option value="CBN_Blacklist">CBN Blacklist</option>
              <option value="Internal_Fraud">Internal Fraud</option>
              <option value="Internal_Suspicious">Internal Suspicious</option>
            </select>
            <input type="text" placeholder="Search..." style={{ ...styles.input, flex: 1 }}
              value={watchlistFilter.search} onChange={(e) => setWatchlistFilter({ ...watchlistFilter, search: e.target.value })} />
          </div>
          {watchlistEntries.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Severity</th>
                    <th style={styles.th}>Reason</th>
                    <th style={styles.th}>Listed</th>
                  </tr>
                </thead>
                <tbody>
                  {watchlistEntries.map(entry => (
                    <tr key={entry.id}>
                      <td style={styles.td}><strong>{entry.fullName}</strong></td>
                      <td style={styles.td}><span style={{ ...styles.badge, background: "#0891b220", color: "#0891b2" }}>{entry.watchlistType.replace(/_/g, " ")}</span></td>
                      <td style={styles.td}><span style={{ ...styles.badge, background: `${getSeverityColor(entry.severity)}20`, color: getSeverityColor(entry.severity) }}>{entry.severity}</span></td>
                      <td style={styles.td}><small>{entry.reason || "-"}</small></td>
                      <td style={styles.td}>{formatDate(entry.listedDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No watchlist entries found</p>
          )}
        </div>
      )}

      {/* SAR Tab */}
      {activeTab === "sar" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>📑 Suspicious Activity Reports (SAR)</h3>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => setShowSarModal(true)}>+ Create SAR</button>
          </div>
          {sars.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Report #</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Submitted To</th>
                    <th style={styles.th}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {sars.map(sar => (
                    <tr key={sar.id}>
                      <td style={styles.td}><strong>{sar.reportNumber}</strong></td>
                      <td style={styles.td}>{sar.reportType}</td>
                      <td style={styles.td}>{sar.userName}</td>
                      <td style={styles.td}>{sar.totalAmountInvolved ? formatCurrency(sar.totalAmountInvolved) : "-"}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, 
                          background: sar.status === "Draft" ? "#e5e7eb" : sar.status === "Submitted" ? "#fef3c7" : "#dcfce7",
                          color: sar.status === "Draft" ? "#374151" : sar.status === "Submitted" ? "#d97706" : "#16a34a" }}>
                          {sar.status}
                        </span>
                      </td>
                      <td style={styles.td}>{sar.submittedTo || "-"}</td>
                      <td style={styles.td}>{formatDate(sar.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No SAR reports</p>
          )}
        </div>
      )}

      {/* Screen User Modal */}
      {showScreenModal && (
        <div style={styles.modal} onClick={() => setShowScreenModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🔍 Screen User</h3>
              <button style={styles.closeBtn} onClick={() => setShowScreenModal(false)}>×</button>
            </div>
            <form onSubmit={handleScreenUser} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>User ID *</label>
                <input type="number" style={styles.input} required value={screenForm.userId}
                  onChange={(e) => setScreenForm({ ...screenForm, userId: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name (optional override)</label>
                <input type="text" style={styles.input} value={screenForm.fullName}
                  onChange={(e) => setScreenForm({ ...screenForm, fullName: e.target.value })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>BVN</label>
                  <input type="text" style={styles.input} maxLength={11} value={screenForm.bvn}
                    onChange={(e) => setScreenForm({ ...screenForm, bvn: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>NIN</label>
                  <input type="text" style={styles.input} maxLength={11} value={screenForm.nin}
                    onChange={(e) => setScreenForm({ ...screenForm, nin: e.target.value })} />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Screening Options</label>
                <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                  <label style={styles.checkbox}>
                    <input type="checkbox" checked={screenForm.screenPep} onChange={(e) => setScreenForm({ ...screenForm, screenPep: e.target.checked })} />
                    <span>🏛️ PEP Database</span>
                  </label>
                  <label style={styles.checkbox}>
                    <input type="checkbox" checked={screenForm.screenWatchlist} onChange={(e) => setScreenForm({ ...screenForm, screenWatchlist: e.target.checked })} />
                    <span>📝 Watchlist</span>
                  </label>
                  <label style={styles.checkbox}>
                    <input type="checkbox" checked={screenForm.screenSanctions} onChange={(e) => setScreenForm({ ...screenForm, screenSanctions: e.target.checked })} />
                    <span>🚫 Sanctions</span>
                  </label>
                </div>
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "14px" }}>
                🔍 Run Screening
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Add PEP Modal */}
      {showPepModal && (
        <div style={styles.modal} onClick={() => setShowPepModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🏛️ Add PEP Entry</h3>
              <button style={styles.closeBtn} onClick={() => setShowPepModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddPep} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name *</label>
                <input type="text" style={styles.input} required value={pepForm.fullName}
                  onChange={(e) => setPepForm({ ...pepForm, fullName: e.target.value })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Position</label>
                  <input type="text" style={styles.input} value={pepForm.position}
                    onChange={(e) => setPepForm({ ...pepForm, position: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Organization</label>
                  <input type="text" style={styles.input} value={pepForm.organization}
                    onChange={(e) => setPepForm({ ...pepForm, organization: e.target.value })} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Category</label>
                  <select style={styles.select} value={pepForm.pepCategory}
                    onChange={(e) => setPepForm({ ...pepForm, pepCategory: e.target.value })}>
                    <option value="Domestic">Domestic</option>
                    <option value="Foreign">Foreign</option>
                    <option value="International">International</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Level</label>
                  <select style={styles.select} value={pepForm.pepLevel}
                    onChange={(e) => setPepForm({ ...pepForm, pepLevel: e.target.value })}>
                    <option value="Primary">Primary (Direct)</option>
                    <option value="Family">Family Member</option>
                    <option value="Associate">Close Associate</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Country</label>
                  <input type="text" style={styles.input} value={pepForm.country}
                    onChange={(e) => setPepForm({ ...pepForm, country: e.target.value })} />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea style={styles.textarea} value={pepForm.notes}
                  onChange={(e) => setPepForm({ ...pepForm, notes: e.target.value })} />
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "14px" }}>
                ✅ Add PEP Entry
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Add Watchlist Modal */}
      {showWatchlistModal && (
        <div style={styles.modal} onClick={() => setShowWatchlistModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>📝 Add Watchlist Entry</h3>
              <button style={styles.closeBtn} onClick={() => setShowWatchlistModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddWatchlist} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name *</label>
                <input type="text" style={styles.input} required value={watchlistForm.fullName}
                  onChange={(e) => setWatchlistForm({ ...watchlistForm, fullName: e.target.value })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Watchlist Type *</label>
                  <select style={styles.select} required value={watchlistForm.watchlistType}
                    onChange={(e) => setWatchlistForm({ ...watchlistForm, watchlistType: e.target.value })}>
                    <option value="">Select Type</option>
                    <option value="EFCC">EFCC (Economic & Financial Crimes)</option>
                    <option value="ICPC">ICPC (Corrupt Practices)</option>
                    <option value="CBN_Blacklist">CBN Blacklist</option>
                    <option value="Internal_Fraud">Internal Fraud</option>
                    <option value="Internal_Suspicious">Internal Suspicious</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Severity *</label>
                  <select style={styles.select} value={watchlistForm.severity}
                    onChange={(e) => setWatchlistForm({ ...watchlistForm, severity: e.target.value })}>
                    <option value="Critical">Critical</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Reason *</label>
                <textarea style={styles.textarea} required value={watchlistForm.reason}
                  onChange={(e) => setWatchlistForm({ ...watchlistForm, reason: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Case Reference</label>
                <input type="text" style={styles.input} value={watchlistForm.caseReference}
                  onChange={(e) => setWatchlistForm({ ...watchlistForm, caseReference: e.target.value })} />
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "14px" }}>
                ✅ Add to Watchlist
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Create SAR Modal */}
      {showSarModal && (
        <div style={styles.modal} onClick={() => setShowSarModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>📑 Create SAR</h3>
              <button style={styles.closeBtn} onClick={() => setShowSarModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateSar} style={styles.form}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>User ID *</label>
                  <input type="number" style={styles.input} required value={sarForm.userId}
                    onChange={(e) => setSarForm({ ...sarForm, userId: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Report Type</label>
                  <select style={styles.select} value={sarForm.reportType}
                    onChange={(e) => setSarForm({ ...sarForm, reportType: e.target.value })}>
                    <option value="SAR">SAR (Suspicious Activity)</option>
                    <option value="STR">STR (Suspicious Transaction)</option>
                    <option value="CTR">CTR (Currency Transaction)</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Suspicious Activity *</label>
                <textarea style={styles.textarea} required value={sarForm.suspiciousActivity}
                  onChange={(e) => setSarForm({ ...sarForm, suspiciousActivity: e.target.value })}
                  placeholder="Describe the suspicious activity in detail..." />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Total Amount Involved (₦)</label>
                <input type="number" style={styles.input} value={sarForm.totalAmountInvolved}
                  onChange={(e) => setSarForm({ ...sarForm, totalAmountInvolved: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea style={styles.textarea} value={sarForm.notes}
                  onChange={(e) => setSarForm({ ...sarForm, notes: e.target.value })} />
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "14px" }}>
                📑 Create SAR
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Risk Profile Modal */}
      {showProfileModal && selectedProfile && (
        <div style={styles.modal} onClick={() => setShowProfileModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>👤 Risk Profile - {selectedProfile.userName}</h3>
              <button style={styles.closeBtn} onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            <div style={{ display: "grid", gap: "15px" }}>
              <div style={{ padding: "15px", background: `${getRiskLevelColor(selectedProfile.riskLevel)}10`, borderRadius: "8px", border: `1px solid ${getRiskLevelColor(selectedProfile.riskLevel)}40` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "18px", fontWeight: "bold" }}>Risk Level: {selectedProfile.riskLevel}</span>
                  <span style={{ fontSize: "24px", fontWeight: "bold", color: getRiskLevelColor(selectedProfile.riskLevel) }}>{selectedProfile.overallRiskScore}/100</span>
                </div>
              </div>
              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                <div style={{ padding: "10px", background: "#f9fafb", borderRadius: "8px" }}>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>PEP Score</div>
                  <div style={{ fontWeight: "bold" }}>{selectedProfile.pepRiskScore}</div>
                </div>
                <div style={{ padding: "10px", background: "#f9fafb", borderRadius: "8px" }}>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>Watchlist Score</div>
                  <div style={{ fontWeight: "bold" }}>{selectedProfile.watchlistRiskScore}</div>
                </div>
                <div style={{ padding: "10px", background: "#f9fafb", borderRadius: "8px" }}>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>Transaction Score</div>
                  <div style={{ fontWeight: "bold" }}>{selectedProfile.transactionRiskScore}</div>
                </div>
              </div>

              <div>
                <h4>Status Flags</h4>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {selectedProfile.isPep && <span style={{ ...styles.badge, background: "#7c3aed20", color: "#7c3aed" }}>🏛️ PEP</span>}
                  {selectedProfile.isPepFamily && <span style={{ ...styles.badge, background: "#7c3aed20", color: "#7c3aed" }}>👨‍👩‍👧 PEP Family</span>}
                  {selectedProfile.isWatchlisted && <span style={{ ...styles.badge, background: "#dc262620", color: "#dc2626" }}>📝 Watchlisted</span>}
                  {selectedProfile.isSanctioned && <span style={{ ...styles.badge, background: "#dc262620", color: "#dc2626" }}>🚫 Sanctioned</span>}
                  {selectedProfile.transactionsBlocked && <span style={{ ...styles.badge, background: "#dc262620", color: "#dc2626" }}>🚫 Blocked</span>}
                  {selectedProfile.requiresEnhancedDueDiligence && <span style={{ ...styles.badge, background: "#d9770620", color: "#d97706" }}>⚠️ EDD Required</span>}
                  {selectedProfile.isUnderMonitoring && <span style={{ ...styles.badge, background: "#0891b220", color: "#0891b2" }}>👁️ Monitored</span>}
                </div>
              </div>

              {selectedProfile.transactionLimitMultiplier < 1 && (
                <div style={{ padding: "10px", background: "#fef3c7", borderRadius: "8px" }}>
                  <strong>⚠️ Transaction Limit Reduced:</strong> {(selectedProfile.transactionLimitMultiplier * 100).toFixed(0)}% of normal limits
                </div>
              )}

              <div>
                <h4>Screening Info</h4>
                <p><strong>Last Screened:</strong> {formatDate(selectedProfile.lastScreeningDate)}</p>
                <p><strong>Next Screening Due:</strong> {formatDate(selectedProfile.nextScreeningDue)}</p>
              </div>

              {selectedProfile.riskNotes && (
                <div>
                  <h4>Notes</h4>
                  <p>{selectedProfile.riskNotes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
